'use client'

import { useEffect, useState } from 'react'
import NavBar from '@/components/navbar'
import PageTitle from '@/components/page-title'
import OfflineBanner from '@/components/offline-banner'
import Tournaments from '@/components/tournaments'
import { getSavedTournament } from '@/components/utils/apiHelper'
import '../index.css'
import '@/app/globals.css'
import SunMoon from '@/components/utils/sun-moon'
import AxelerantLogo from '@/components/utils/axl-logo'
import '@fortawesome/fontawesome-svg-core/styles.css'
import { config } from '@fortawesome/fontawesome-svg-core'
config.autoAddCss = false

export default function RootLayout({ children }) {
  const [online, setOnline] = useState(true)
  const [savedTournament, setSavedTournament] = useState(null)
  const [colorMode, setColorMode] = useState(() => {
    if (typeof window !== 'undefined') {
      const value = localStorage.getItem('darkModeCheckbox');
      return value === 'light';
    }
    return false;
  })

  useEffect(() => {
    const handleOnline = () => setOnline(true)
    const handleOffline = () => setOnline(false)
    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)
    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [])

  useEffect(() => {
    const handleColorModeChanged = (event) => {
      setColorMode(!!event.detail);
    }
    document.addEventListener('colorModeChanged', handleColorModeChanged)
    // Also listen for direct localStorage changes (e.g. from another tab)
    const handleStorage = (event) => {
      if (event.key === 'darkModeCheckbox') {
        setColorMode(event.newValue === 'light');
      }
    }
    window.addEventListener('storage', handleStorage)
    return () => {
      document.removeEventListener('colorModeChanged', handleColorModeChanged)
      window.removeEventListener('storage', handleStorage)
    }
  }, [])

  useEffect(() => {
    // Set savedTournament from localStorage on client only
    setSavedTournament(getSavedTournament());
  }, []);

  // Handler to update tournament selection
  const handleSetTournamentId = (tid) => {
    setSavedTournament(tid)
    // Optionally, you can also reset other state or trigger reloads here
  }

  return (
    <html lang="en">
      <head>
        <link rel="preconnect" href="https://sheets.google.com" />
        <link rel="preconnect" href="https://www.google-analytics.com" />
        <link rel="preconnect" href="https://www.googleapis.com" />
        {/* Google tag (gtag.js) */}
        <script async src="https://www.googletagmanager.com/gtag/js?id=G-WKDS0V1DHR"></script>
        <script dangerouslySetInnerHTML={{__html: `
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          gtag('js', new Date());
          gtag('config', 'G-WKDS0V1DHR');
        `}} />
        <meta charSet="utf-8" />
        <link rel="icon" href="/favicon.webp" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta name="theme-color" content="#462255" />
        {/* Primary Meta Tags */}
        <title>IPL 2025 | Axelerant Prediction Pro</title>
        <meta name="title" content="IPL 2025 | Axelerant Prediction Pro" />
        <meta name="description" content="Axelerant IPL Prediction Pro is the prediction game for the T20 cricket league (Indian Premier League) exclusively designed for cricket fans at Axelerant and to engage them with the sport they love deeply." />
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://iplpredictionpro.axelerant.com/" />
        <meta property="og:title" content="IPL 2025 | Axelerant Prediction Pro" />
        <meta property="og:description" content="Axelerant IPL Prediction Pro is the prediction game for the T20 cricket league (Indian Premier League) exclusively designed for cricket fans at Axelerant and to engage them with the sport they love deeply." />
        <meta property="og:image" content="/logo.png" />
        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content="https://iplpredictionpro.axelerant.com/" />
        <meta property="twitter:title" content="IPL 2025 | Axelerant Prediction Pro" />
        <meta property="twitter:description" content="Axelerant IPL Prediction Pro is the prediction game for the T20 cricket league (Indian Premier League) exclusively designed for cricket fans at Axelerant and to engage them with the sport they love deeply." />
        <meta property="twitter:image" content="/logo.png" />
        <link rel="manifest" href="/manifest.json" />
      </head>
      <body>
        <main className="main-page-wrapper">
          <div className="header">
            <div className="page-title-block">
              <Tournaments savedTournament={savedTournament} setTournamentId={handleSetTournamentId} />
              <SunMoon />
              <AxelerantLogo colorMode={colorMode} />
              <PageTitle savedTournament={savedTournament} />
            </div>
          </div>
          <NavBar />
          {!online && (
            <OfflineBanner>
              App is operating in offline mode, data being displayed from local cache!
            </OfflineBanner>
          )}
          <div className="main-page-content">
            {children}
          </div>
          <div className="copyright">
            © {`402${new Date().getFullYear().toString().slice(-1)}`} Axelerant. All Rights Reserved.
          </div>
        </main>
      </body>
    </html>
  )
}